import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Copy, Check, AlertTriangle, ExternalLink, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";

export const WifiCard = ({ wifi }) => {
  const [copied, setCopied] = useState(null);
  const copy = async (value, which) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(which);
    toast.success(which === "pw" ? "Wi-Fi password copied" : "Network name copied");
    setTimeout(() => setCopied(null), 2000);
  };
  return (
    <div className="mt-4 grid sm:grid-cols-2 gap-3">
      <div className="bg-cream rounded-xl px-4 py-3 border border-charcoal/10">
        <p className="text-[11px] uppercase tracking-[0.14em] text-soft font-semibold mb-1">Network</p>
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-charcoal text-lg" data-testid="wifi-ssid">{wifi.ssid}</span>
          <button onClick={() => copy(wifi.ssid, "ssid")} className="p-2 rounded-full hover:bg-sand transition-colors" aria-label="Copy network name" data-testid="wifi-copy-ssid-button">
            {copied === "ssid" ? <Check size={16} className="text-sagedeep" /> : <Copy size={16} className="text-copper" />}
          </button>
        </div>
      </div>
      <div className="bg-cream rounded-xl px-4 py-3 border border-charcoal/10">
        <p className="text-[11px] uppercase tracking-[0.14em] text-soft font-semibold mb-1">Password · case-sensitive</p>
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-charcoal text-lg font-mono" data-testid="wifi-password">{wifi.password}</span>
          <button onClick={() => copy(wifi.password, "pw")} className="p-2 rounded-full hover:bg-sand transition-colors" aria-label="Copy Wi-Fi password" data-testid="guide-wifi-copy-button">
            {copied === "pw" ? <Check size={16} className="text-sagedeep" /> : <Copy size={16} className="text-copper" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export const Panel = ({ panel, open, onToggle, wifi, onOpenConcierge }) => {
  const isAlert = panel.alert;
  return (
    <div
      id={panel.id}
      className={`scroll-mt-40 rounded-2xl border transition-colors ${open ? "shadow-md" : ""} ${
        isAlert ? "bg-[#FBF1EA] border-copper/40" : "bg-white border-charcoal/10"
      }`}
      data-testid={`panel-${panel.id}`}
    >
      <h3>
        <button
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`${panel.id}-body`}
          className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5 min-h-[56px]"
          data-testid={`panel-toggle-${panel.id}`}
        >
          <span className={`flex items-center gap-3 font-serif text-lg sm:text-xl font-semibold ${isAlert ? "text-copperdeep" : "text-charcoal"}`}>
            {isAlert && <AlertTriangle size={20} className="shrink-0 text-copper" aria-hidden />}
            {panel.title}
          </span>
          <ChevronDown size={20} aria-hidden className={`shrink-0 text-soft transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
      </h3>
      <div id={`${panel.id}-body`} role="region" aria-label={panel.title} className={`panel-body ${open ? "open" : ""}`}>
        <div>
          <div className="px-5 sm:px-6 pb-6 space-y-3 text-base text-soft leading-relaxed">
            {(panel.body || []).map((para, i) => <p key={i}>{para}</p>)}
            {panel.bullets && (
              <ul className="space-y-2 pt-1">
                {panel.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-copper shrink-0" aria-hidden />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {panel.type === "wifi" && wifi && <WifiCard wifi={wifi} />}
            {panel.photos && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-2" data-testid={`panel-photos-${panel.id}`}>
                {panel.photos.map((ph, i) => (
                  <img key={ph.url} src={ph.url} alt={ph.alt} loading="lazy"
                    className="w-full aspect-[4/3] object-cover rounded-xl shadow-sm"
                    data-testid={`panel-photo-${panel.id}-${i}`} />
                ))}
              </div>
            )}
            {panel.type === "concierge" && (
              <button
                onClick={onOpenConcierge}
                className="inline-flex items-center gap-2 bg-copper text-cream rounded-full px-5 py-3 font-semibold hover:bg-copperdeep transition-colors"
                data-testid="panel-ask-guide-button"
              >
                <MessageCircleQuestion size={18} aria-hidden /> Ask the guide
              </button>
            )}
            {panel.links && (
              <div className="flex flex-wrap gap-2.5 pt-2">
                {panel.links.map((l) => (
                  <a key={l.label} href={l.url}
                    className="inline-flex items-center gap-1.5 bg-charcoal text-cream rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-copperdeep transition-colors"
                    data-testid={`panel-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {l.label} <ExternalLink size={14} aria-hidden />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const GuideSection = ({ section, index, activePanel, onToggle, wifi, onOpenConcierge }) => (
  <section id={`section-${section.id}`} className="scroll-mt-32" aria-label={section.title} data-testid={`section-${section.id}`}>
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-baseline gap-4 mb-6"
    >
      <span className="font-serif italic text-copper text-xl" aria-hidden>{String(index + 1).padStart(2, "0")}</span>
      <h2 className="font-sans text-2xl sm:text-3xl font-extrabold tracking-tight text-charcoal uppercase">{section.title}</h2>
    </motion.div>
    <div className="space-y-3">
      {section.panels.map((panel) => (
        <Panel
          key={panel.id}
          panel={panel}
          open={activePanel === panel.id}
          onToggle={() => onToggle(panel.id)}
          wifi={wifi}
          onOpenConcierge={onOpenConcierge}
        />
      ))}
    </div>
  </section>
);
